import { Icon } from '@buggy/shared'
import { Divider, Flex, Space, Tag, Typography } from 'antd'
import { isEmpty, isNull } from 'lodash-es'
import { useState, type FunctionComponent } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import styled from 'styled-components'
import PostImage, {
  PostImageWrapper,
} from '@/components/common/image/post-image'
import PostCardStatistic from '@/components/common/post/post-card-statistic'
import PostTypeTag from '@/components/common/post/post-type-tag'
import SocialMediaPostModal, {
  type SocialMediaPost,
} from '@/components/modal/social-media-post-modal'
import useFormatPrice from '@/hooks/use-format-price'
import useWorkspaceDollarSign from '@/hooks/use-workspace-dollar-sign'
import { useIntl } from '@/i18n/hooks/use-intl'
import type { Optional } from '@/types/optional'
import {
  CollaborationRecordScoringType,
  CustomPost,
  Post,
} from '@/types/schema/directory/collaboration-record'
import { convertPlatformShortcode } from '@/utils/convert-platform'
import getPostThumbnailUrls from '@/utils/kol/get-post-thumbnail-urls'
import isCustomPost from '@/utils/types/is-custom-post'

interface PostListProps {
  kolId: string
  posts: (Post | CustomPost)[]
  collaborationRecordScoringType: CollaborationRecordScoringType
}

const PostList: FunctionComponent<PostListProps> = ({
  kolId,
  posts,
  collaborationRecordScoringType,
}) => {
  const { formatMessage, formatNumber } = useIntl()
  const { formatPrice } = useFormatPrice()

  const workspaceDollarSign = useWorkspaceDollarSign()

  const [selectedPost, setSelectedPost] = useState<SocialMediaPost>()

  return (
    <>
      <Flex vertical gap={18}>
        {posts.map((post) => {
          const isCustom = isCustomPost(post)
          const platform =
            isCustom && post.is_custom_platform
              ? undefined
              : convertPlatformShortcode(post.platform)
          const isStatisticEmpty = [
            post.play_count,
            post.like_count,
            post.comment_count,
            post.share_count,
            post.view_count,
          ].every(isNull)
          const title =
            (post.title?.trim() ?? '') + (post.content?.trim() ?? '')
          const showOtherStatistic = !!post.reach_count || !!post.click_count
          const socialMediaPost: Optional<SocialMediaPost> =
            platform?.code && post.crawled_id && post.url && post.post_id
              ? {
                  platformCode: platform.code,
                  url: post.url,
                  crawledID: post.crawled_id,
                  postID: post.post_id,
                  postType: post.post_type,
                  platformTitle: platform?.title,
                }
              : undefined
          const handleClickPost = socialMediaPost
            ? (event: React.MouseEvent<HTMLDivElement>): void => {
                event.stopPropagation()
                setSelectedPost(socialMediaPost)
              }
            : undefined

          return (
            <PostCard
              key={post.id}
              role={handleClickPost ? 'button' : undefined}
              onClick={handleClickPost}
            >
              <AutoSizer disableWidth>
                {({ height }) => {
                  if (isNull(post.thumbnail_url)) {
                    return null
                  }

                  return (
                    <PostImage
                      height={height}
                      kolUuid={kolId}
                      thumbnailUrls={getPostThumbnailUrls({
                        platformType: platform?.type ?? 0,
                        crawledID: post.crawled_id ?? '',
                        postID: post.post_id ?? '',
                        igPostId: post.ig_post_id ?? '',
                        thumbnail: post.thumbnail_url,
                      })}
                      onClick={handleClickPost}
                    />
                  )
                }}
              </AutoSizer>
              <Flex vertical gap={8} style={{ padding: 16 }}>
                <Flex align='center' gap={4}>
                  {platform?.sharedIconName && (
                    <Icon fontSize={20} name={platform.sharedIconName} />
                  )}
                  <PostTypeTag
                    isCustomPlatform={isCustom && post.is_custom_platform}
                    isCustomPostType={isCustom && post.is_custom_post_type}
                    platform={post.platform}
                    postType={post.post_type}
                  />
                  {collaborationRecordScoringType !==
                    CollaborationRecordScoringType.Customized && (
                    <Reward>
                      {isNull(post.reward)
                        ? `${workspaceDollarSign} -`
                        : formatPrice(post.reward)}
                    </Reward>
                  )}
                </Flex>
                {!isEmpty(title) && (
                  <Title ellipsis={{ rows: 2 }}>{title}</Title>
                )}
                <Flex align='center' gap={8}>
                  {isCustom && post.is_use_handfilled && (
                    <Tag>
                      {formatMessage({
                        id: 'general:cooperation_item_type_custom_data',
                      })}
                    </Tag>
                  )}
                  {isStatisticEmpty ? (
                    <OtherStatistic>
                      {formatMessage({
                        id: 'autoreport:chart_label_empty_data',
                      })}
                    </OtherStatistic>
                  ) : (
                    <PostCardStatistic
                      commentCount={post.comment_count}
                      isDisableLike={post.is_disable_like ?? false}
                      likeCount={post.like_count}
                      platformShortCode={platform?.shortcode}
                      playCount={post.play_count}
                      shareCount={post.share_count}
                      viewCount={post.view_count}
                    />
                  )}
                </Flex>
                {showOtherStatistic && (
                  <Space align='center' split={<Divider type='vertical' />}>
                    {!!post.reach_count && (
                      <OtherStatistic>
                        {[
                          formatMessage({
                            id: 'general:column_title_touch_count',
                          }),
                          formatMessage({
                            id: 'general:colon',
                          }),
                          formatNumber(post.reach_count),
                        ].join('')}
                      </OtherStatistic>
                    )}
                    {!!post.click_count && (
                      <OtherStatistic>
                        {[
                          formatMessage({
                            id: 'general:column_title_link_click_count',
                          }),
                          formatMessage({
                            id: 'general:colon',
                          }),
                          formatNumber(post.click_count),
                        ].join('')}
                      </OtherStatistic>
                    )}
                  </Space>
                )}
              </Flex>
            </PostCard>
          )
        })}
      </Flex>
      {selectedPost && (
        <SocialMediaPostModal
          post={selectedPost}
          onCancel={(): void => setSelectedPost(undefined)}
        />
      )}
    </>
  )
}

const PostCard = styled.div`
  cursor: ${({ role }): string => (role === 'button' ? 'pointer' : 'default')};
  display: flex;
  border-radius: 6px;
  border: 1px solid ${({ theme }): string => theme.colors.border.border};
  overflow: hidden;
  background-color: ${({ theme }): string => theme.colors.background.white};

  .ant-tag {
    margin-inline-end: 0;
  }

  .ant-divider {
    margin: 0;
    border-color: ${({ theme }): string => theme.colors.text.tooltip};
  }

  ${PostImageWrapper} {
    aspect-ratio: 160 / 146;
    max-width: 160px;
  }
`

const Reward = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 13px;
  line-height: 20px;
`

const Title = styled(Typography.Paragraph)`
  &.ant-typography {
    color: ${({ theme }): string => theme.colors.text.primary};
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
    white-space: normal;
  }
`

const OtherStatistic = styled.div`
  color: ${({ theme }): string => theme.colors.text.tooltip};
  font-size: 12px;
  line-height: 18px;
`

export default PostList
